'use client';

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { format, parseISO } from 'date-fns';
import { es, enUS, pt } from 'date-fns/locale';
import { useUnitsWithCache } from '@/hooks/useUnitsCache';
import { Unit } from '@/lib/admin/units-service';
import { lodges } from '@/lib/data/lodges';
import { useActivityVisibility } from '@/hooks/useActivityVisibility';
import { useSoldOut } from '@/hooks/useSoldOut';
import { useExchangeRate } from '@/lib/hooks/useExchangeRate';
import LodgeSearchBar from '@/components/booking/LodgeSearchBar';
import LodgeSearchResults from '../LodgeSearchResults';
import { searchLodges, LodgeSearchResponse } from '@/lib/booking/search-service';
import styles from '../page.module.css';

type UnitTypeFilter = 'all' | 'room' | 'full_lodge' | 'premium';

interface Props {
  lodgeId: string;
}

// Dates + guests currently being searched within this lodge. Mirrors the
// shape used by booking/lodges/page.tsx, kept local here since the scoped
// search only ever runs against this lodge's locationId.
interface SearchParamsState {
  checkIn: string; // YYYY-MM-DD
  checkOut: string; // YYYY-MM-DD
  guests: number;
}

function isAbortError(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { name?: string }).name === 'AbortError';
}

function formatUSD(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function getUnitImage(unit: Unit): string {
  if (unit.images && unit.images.length > 0) {
    return unit.images[0];
  }
  return 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80';
}

export default function LodgeUnitsClient({ lodgeId }: Props) {
  const t = useTranslations('bookingLodges');
  const locale = useLocale() as 'en' | 'es' | 'pt';
  const dateFnsLocale = locale === 'es' ? es : locale === 'pt' ? pt : enUS;
  const router = useRouter();
  const { isVisible, loaded: visibilityLoaded } = useActivityVisibility();
  const { isSoldOut } = useSoldOut();
  const { convertCLPtoUSD } = useExchangeRate();
  const [typeFilter, setTypeFilter] = useState<UnitTypeFilter>('all');

  // Search bar scoped to this lodge: same shape/flow as booking/lodges/page.tsx
  // (AbortController with an identity guard so a stale response can never
  // clobber a newer one), but every call is pinned to this lodge's locationId.
  const [search, setSearch] = useState<SearchParamsState | null>(null);
  const [results, setResults] = useState<LodgeSearchResponse | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const searchRef = useRef<SearchParamsState | null>(null);

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  const runSearch = useCallback(async (params: SearchParamsState) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setSearchLoading(true);
    setSearchError(null);

    try {
      const data = await searchLodges({ ...params, locationId: lodgeId }, controller.signal);
      if (abortControllerRef.current !== controller) return; // superseded by a newer search
      setResults(data);
      setSearchLoading(false);
    } catch (err) {
      if (isAbortError(err)) return; // cancelled by a newer search, ignore
      if (abortControllerRef.current !== controller) return;
      setResults(null);
      setSearchError(err instanceof Error ? err.message : 'No se pudo buscar disponibilidad');
      setSearchLoading(false);
    }
  }, [lodgeId]);

  // Abort any in-flight search when the component unmounts.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Re-run an active search when the scope itself changes (e.g. browser
  // back/forward between two lodge slugs without a full remount). Skipped
  // on the initial mount — there's nothing to re-run yet.
  const isFirstLodgeRender = useRef(true);
  useEffect(() => {
    if (isFirstLodgeRender.current) {
      isFirstLodgeRender.current = false;
      return;
    }
    if (searchRef.current) {
      runSearch(searchRef.current);
    }
  }, [lodgeId, runSearch]);

  const handleSearch = useCallback((params: SearchParamsState) => {
    setSearch(params);
    runSearch(params);
  }, [runSearch]);

  const handleClearSearch = useCallback(() => {
    abortControllerRef.current?.abort();
    setSearch(null);
    setResults(null);
    setSearchError(null);
    setSearchLoading(false);
  }, []);

  const lodge = useMemo(() => lodges.find((l) => l.id === lodgeId) || null, [lodgeId]);

  // If lodge is hidden by visibility settings or comingSoon, send back to grid
  useEffect(() => {
    if (!lodge) {
      router.replace('/booking/lodges/');
      return;
    }
    if (visibilityLoaded && !isVisible(lodge.id, lodge.comingSoon)) {
      router.replace('/booking/lodges/');
    }
  }, [lodge, isVisible, visibilityLoaded, router]);

  const { units, loading: unitsLoading } = useUnitsWithCache({
    active: 'true',
    sortBy: 'sortOrder',
    sortDir: 'asc',
  });

  const lodgeUnits = useMemo(() => {
    return units.filter((u) => u.locationId === lodgeId);
  }, [units, lodgeId]);

  const filteredUnits = useMemo(() => {
    if (typeFilter === 'all') return lodgeUnits;
    return lodgeUnits.filter((u) => (u.unitType || 'room') === typeFilter);
  }, [lodgeUnits, typeFilter]);

  const typeCounts = useMemo(() => ({
    all: lodgeUnits.length,
    room: lodgeUnits.filter((u) => (u.unitType || 'room') === 'room').length,
    full_lodge: lodgeUnits.filter((u) => (u.unitType || 'room') === 'full_lodge').length,
    premium: lodgeUnits.filter((u) => (u.unitType as string) === 'premium').length,
  }), [lodgeUnits]);

  if (!lodge) {
    return (
      <div className={styles.page}>
        <section className={styles.mainSection}>
          <div className={styles.container}>
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
            </div>
          </div>
        </section>
      </div>
    );
  }

  const distinctTypes = [typeCounts.room > 0, typeCounts.full_lodge > 0, typeCounts.premium > 0].filter(Boolean).length;

  return (
    <div className={styles.page}>
      <section className={styles.hero} data-hero-background>
        <div className={styles.heroContent}>
          <nav className={styles.breadcrumb}>
            <Link href="/booking">{t('breadcrumb.booking')}</Link>
            <span>/</span>
            <Link href="/booking/lodges/">{t('breadcrumb.lodges')}</Link>
            <span>/</span>
            <span>{lodge.name}</span>
          </nav>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>{t('subtitle')}</p>
        </div>
      </section>

      <section className={styles.mainSection}>
        <div className={styles.container}>
          <div className={styles.selectedView}>
            <div className={styles.locationHeader}>
              <Link
                href="/booking/lodges/"
                className={styles.backButton}
                aria-label={t('backToAllLocations')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </Link>
              <div className={styles.headerThumbnailWrap}>
                <Image src={lodge.heroImage} alt={lodge.name} fill sizes="48px" className={styles.headerThumbnail} />
              </div>
              <div className={styles.headerText}>
                <span className={styles.headerName}>{lodge.name}</span>
                <span className={styles.headerPlace}>{lodge.location}</span>
              </div>
            </div>

            <LodgeSearchBar
              initialCheckIn={search?.checkIn}
              initialCheckOut={search?.checkOut}
              initialGuests={search?.guests}
              loading={searchLoading}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />

            {search !== null ? (
              searchLoading ? (
                <div className={styles.loadingState} style={{ marginTop: '2rem' }}>
                  <div className={styles.spinner} />
                </div>
              ) : searchError ? (
                <div className={styles.noUnits} style={{ marginTop: '2rem' }}>
                  <p>{t('search.error')}</p>
                  <button
                    className={styles.contactLink}
                    type="button"
                    onClick={() => search && runSearch(search)}
                  >
                    {t('search.retry')}
                  </button>
                </div>
              ) : results && results.results.length === 0 ? (
                <div className={styles.noUnits} style={{ marginTop: '2rem' }}>
                  <p>{t('search.noResults')}</p>
                  <p>{t('search.noResultsHint')}</p>
                  <button className={styles.contactLink} type="button" onClick={handleClearSearch}>
                    {t('search.clear')}
                  </button>
                </div>
              ) : results ? (
                <div style={{ marginTop: '2rem' }}>
                  <p className={styles.locationPlace}>
                    {t('search.resultsCount', { count: results.results.length })}{' '}
                    {t('search.forDates', {
                      checkIn: format(parseISO(results.startDate), 'd MMM yyyy', { locale: dateFnsLocale }),
                      checkOut: format(parseISO(results.endDate), 'd MMM yyyy', { locale: dateFnsLocale }),
                    })}
                  </p>
                  <LodgeSearchResults
                    results={results.results}
                    nights={results.nights}
                    checkIn={results.startDate}
                    checkOut={results.endDate}
                    guests={results.guests}
                    scopedToLodge
                  />
                </div>
              ) : null
            ) : (
              <>
            {distinctTypes >= 2 && (
              <div className={styles.filterBar}>
                <button
                  className={`${styles.filterPill} ${typeFilter === 'all' ? styles.filterPillActive : ''}`}
                  onClick={() => setTypeFilter('all')}
                  type="button"
                >
                  {t('filter.all')} ({typeCounts.all})
                </button>
                {typeCounts.room > 0 && (
                  <button
                    className={`${styles.filterPill} ${styles.filterPillRoom} ${typeFilter === 'room' ? styles.filterPillActive : ''}`}
                    onClick={() => setTypeFilter('room')}
                    type="button"
                  >
                    <span className={`${styles.filterDot} ${styles.filterDotRoom}`} />
                    {t('filter.rooms')} ({typeCounts.room})
                  </button>
                )}
                {typeCounts.full_lodge > 0 && (
                  <button
                    className={`${styles.filterPill} ${styles.filterPillLodge} ${typeFilter === 'full_lodge' ? styles.filterPillActive : ''}`}
                    onClick={() => setTypeFilter('full_lodge')}
                    type="button"
                  >
                    <span className={`${styles.filterDot} ${styles.filterDotLodge}`} />
                    {t('filter.fullLodge')} ({typeCounts.full_lodge})
                  </button>
                )}
                {typeCounts.premium > 0 && (
                  <button
                    className={`${styles.filterPill} ${styles.filterPillPremium} ${typeFilter === 'premium' ? styles.filterPillActive : ''}`}
                    onClick={() => setTypeFilter('premium')}
                    type="button"
                  >
                    <span className={`${styles.filterDot} ${styles.filterDotPremium}`} />
                    {t('filter.premium')} ({typeCounts.premium})
                  </button>
                )}
              </div>
            )}

            <div className={styles.unitsContainer}>
              {unitsLoading ? (
                <div className={styles.loadingState}>
                  <div className={styles.spinner} />
                </div>
              ) : filteredUnits.length > 0 ? (
                <div className={styles.unitsList}>
                  {filteredUnits.map((unit) => {
                    const unitType = (unit.unitType || 'room') as string;
                    const isPremium = unitType === 'premium';
                    const isFullLodge = unitType === 'full_lodge';
                    const cardClass = isPremium
                      ? styles.unitCardPremium
                      : isFullLodge
                        ? styles.unitCardLodge
                        : styles.unitCardRoom;
                    const badgeClass = isPremium
                      ? styles.typeBadgePremium
                      : isFullLodge
                        ? styles.typeBadgeLodge
                        : styles.typeBadgeRoom;
                    const badgeLabel = isPremium
                      ? t('badge.premium')
                      : isFullLodge
                        ? t('badge.fullLodge')
                        : t('badge.room');
                    const ctaClass = isPremium
                      ? styles.unitCtaPremium
                      : isFullLodge
                        ? styles.unitCtaLodge
                        : styles.unitCtaRoom;
                    const soldOut = isSoldOut(unit.id, false);

                    const cardContent = (
                      <>
                        <div className={styles.unitImageWrap}>
                          <Image src={getUnitImage(unit)} alt={unit.name} fill sizes="(max-width: 768px) 100px, 300px" className={styles.unitImage} />
                          {soldOut && <span className={styles.soldOutBadge}>Sold Out</span>}
                        </div>
                        <div className={styles.unitContent}>
                          <div className={styles.unitNameRow}>
                            <h3 className={styles.unitName}>{unit.name}</h3>
                            <span className={`${styles.typeBadge} ${badgeClass}`}>
                              {isPremium && (
                                <svg className={styles.crownIcon} width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" />
                                  <path d="M5 19h14v2H5z" />
                                </svg>
                              )}
                              {badgeLabel}
                            </span>
                          </div>
                          <p className={styles.unitMeta}>
                            {unit.bedConfiguration} &middot; {unit.maxOccupancy} {t('guests')}
                          </p>
                          {(() => {
                            const rawAmount = unit.pricing.basePrice.amount;
                            const rawCurrency = (unit.pricing.basePrice.currency || '').toUpperCase();
                            const isCLP = rawCurrency === 'CLP' || rawCurrency === 'PESO' || rawCurrency === 'PESOS';
                            const usdAmount = isCLP ? convertCLPtoUSD(rawAmount) : rawAmount;
                            return (
                              <div className={styles.unitPricing}>
                                <span className={styles.fromLabel}>{t('fromPrice')}</span>
                                <span className={styles.unitPrice}>{formatUSD(usdAmount)}</span>
                                <span className={styles.perNight}>/ {t('night')}</span>
                              </div>
                            );
                          })()}
                          {!soldOut && (
                            <span className={`${styles.unitCta} ${ctaClass}`}>
                              {t('viewDetailsBook')}
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14" />
                                <path d="m12 5 7 7-7 7" />
                              </svg>
                            </span>
                          )}
                        </div>
                      </>
                    );

                    if (soldOut) {
                      return (
                        <div
                          key={unit.id}
                          className={`${styles.unitCard} ${cardClass} ${styles.unitCardDisabled}`}
                          aria-disabled="true"
                        >
                          {cardContent}
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={unit.id}
                        href={`/booking/lodges/${unit.id}`}
                        className={`${styles.unitCard} ${cardClass}`}
                      >
                        {cardContent}
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.noUnits}>
                  <p>{typeFilter !== 'all' ? t('noFilteredUnitsMessage') : t('noUnitsMessage')}</p>
                  {typeFilter !== 'all' ? (
                    <button className={styles.contactLink} onClick={() => setTypeFilter('all')} type="button">
                      {t('filter.showAll')}
                    </button>
                  ) : (
                    <Link href="/contact" className={styles.contactLink}>
                      {t('contactConcierge')}
                    </Link>
                  )}
                </div>
              )}
            </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
